import { useCallback, useEffect, useRef, useState } from "react";

import { useSelector } from "react-redux";

import axiosInstance from "../../../utils/axiosInstance";

export const GLANCE_ENDPOINT = "/teacher-onboarding/coach/glance";

// Used only until the first response names the server's own cadence. The server
// is the authority on both values; these keep the very first poll from either
// hammering the endpoint or never firing at all.
export const FALLBACK_POLL_INTERVAL_SECONDS = 60;

// Every attempt is time-boxed. axiosInstance forwards only responseType, params
// and signal, so it sets no timeout of its own: a request that never settles
// would hold the in-flight latch forever and silently disable every poll tick,
// every visibility return, every refreshTrigger refetch and the error card's own
// "try again" for the life of the mount. The surface would sit on the loading
// shell with no recovery path and no indication anything was wrong.
export const REQUEST_TIMEOUT_MS = 20000;

const isAbort = (error) =>
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

const envelopeRoomKey = (envelope) =>
  `${envelope?.cohort?.id ?? "none"}:${envelope?.challenge?.id ?? "none"}`;

/**
 * The Coach glance: one authorized read, polled while the tab is visible.
 *
 * Every write to hook state is guarded by a monotonically increasing request
 * generation, so a slow response that lands after a newer one can never
 * overwrite it. Superseded requests are aborted rather than merely ignored, and
 * the same abort fires on unmount. Four things can trigger a fetch (the poll
 * interval, a visibility return, the Redux refreshTrigger, an explicit refetch
 * or context switch) and all four run through the same guard.
 *
 * A tick that arrives while a request is in flight is SKIPPED, never queued: on
 * a slow connection a queue would turn one stale poll into a backlog of them.
 *
 * @param {{cohortId?: number|null, challengeId?: number|null}} context
 *   The (cohort, challenge) pair to compute the glance for. Omit both to let the
 *   server pick its documented default. Changing it clears the previous
 *   context's data before the new response lands, so a squad grid is never shown
 *   under the wrong cohort's heading.
 */
export const useCoachGlance = (context = {}) => {
  const { cohortId = null, challengeId = null } = context;

  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  const refreshTrigger = useSelector(
    (state) => state.adminDashboard?.refreshTrigger || 0,
  );

  const generationRef = useRef(0);
  const inFlightRef = useRef(null);
  const mountedRef = useRef(true);
  const pendingRef = useRef(false);
  const fetchGlanceRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pendingRef.current = false;
      inFlightRef.current?.abort();
      inFlightRef.current = null;
    };
  }, []);

  /**
   * @param {{queueIfBusy?: boolean}} options
   *   `queueIfBusy` separates the two kinds of caller, and the distinction is
   *   the whole point of this function's shape.
   *
   *   A POLL TICK is not authoritative. Dropping it while a request is already
   *   in flight is correct: on a slow connection, queueing every tick would
   *   build a backlog of requests nobody asked for.
   *
   *   An AUTHORITATIVE REFETCH follows a write the facilitator just made (a
   *   level change, onboarding completion, a join) and the in-flight response
   *   predates that write, so it CANNOT answer the question being asked.
   *   Dropping it silently left the surface showing pre-write state until the
   *   next tick, up to a full poll interval later, which read as the level
   *   selector reverting by itself. So: invalidate the in-flight response by
   *   bumping the generation, and queue exactly one trailing refetch. One,
   *   never a queue of them, so the no-backlog property survives.
   */
  const fetchGlance = useCallback(
    async (options = {}) => {
      if (options.force && inFlightRef.current) {
        pendingRef.current = false;
        generationRef.current += 1;
        inFlightRef.current.abort();
        inFlightRef.current = null;
      }

      if (inFlightRef.current) {
        if (options.queueIfBusy) {
          pendingRef.current = true;
          generationRef.current += 1;
        }
        return null;
      }

      const generation = generationRef.current + 1;
      generationRef.current = generation;

      const controller = new AbortController();
      inFlightRef.current = controller;

      // `timedOut` is per-attempt, so a deliberate abort (unmount, context
      // switch) stays silent while a timeout surfaces as a real error state the
      // facilitator can retry from.
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, REQUEST_TIMEOUT_MS);

      const params = {};
      if (cohortId != null) params.cohort_id = Number(cohortId);
      if (challengeId != null) params.challenge_id = Number(challengeId);

      try {
        const response = await axiosInstance(
          GLANCE_ENDPOINT,
          "get",
          undefined,
          {
            params,
            signal: controller.signal,
          },
        );

        if (!mountedRef.current || generation !== generationRef.current) return;

        const accepted = response?.data || null;
        setData(accepted);
        setError(null);
        setStatus("ready");
        return accepted;
      } catch (e) {
        if (isAbort(e) && !timedOut) return;
        if (!mountedRef.current || generation !== generationRef.current) return;

        setError({
          status: e?.response?.status ?? null,
          message: timedOut ? "timed out" : e?.response?.data?.message || "",
        });
        setStatus("error");
        if (options.rejectOnError) {
          const failure = new Error(
            timedOut ? "glance_timeout" : "glance_failed",
          );
          failure.cause = e;
          throw failure;
        }
      } finally {
        clearTimeout(timeoutId);
        if (inFlightRef.current === controller) inFlightRef.current = null;

        // The trailing refetch. Fires once, against the params in force NOW, so a
        // level change made mid-poll is answered by the next request rather than
        // by the one that was already on the wire when the write happened.
        if (pendingRef.current && mountedRef.current) {
          pendingRef.current = false;
          fetchGlanceRef.current?.({ queueIfBusy: true });
        }
      }
    },
    [cohortId, challengeId],
  );

  // fetchGlance cannot call itself by name from inside its own definition, and
  // the trailing refetch must use the LATEST closure (new params after a context
  // switch), never the one that started the settled request.
  fetchGlanceRef.current = fetchGlance;

  // The exposed refetch is always authoritative: every caller is a retry button,
  // a post-write refresh, or a visibility return. It also swallows its caller's
  // arguments, so an `onJoined={refetch}` handler cannot pass a challenge object
  // in where the options object belongs.
  const refetch = useCallback(
    () => fetchGlance({ queueIfBusy: true }),
    [fetchGlance],
  );

  // A pace write needs the envelope that was actually accepted into provider
  // state, not a fire-and-forget refresh. This operation cancels an older poll,
  // starts one authoritative request through the same coordinator, and rejects
  // if the room identity no longer matches the confirmation that opened it.
  const refreshSnapshot = useCallback(
    async (expectedRoomKey) => {
      const accepted = await fetchGlance({
        force: true,
        rejectOnError: true,
      });
      if (!accepted) throw new Error("glance_not_accepted");
      if (
        expectedRoomKey &&
        envelopeRoomKey(accepted) !== String(expectedRoomKey)
      ) {
        throw new Error("room_changed");
      }
      return accepted;
    },
    [fetchGlance],
  );

  // Context identity reset. The component stays mounted across a switch, so the
  // previous cohort's squads have to be cleared explicitly; the in-flight
  // request for the old context is aborted rather than left to resolve into a
  // heading that no longer describes it.
  useEffect(() => {
    inFlightRef.current?.abort();
    inFlightRef.current = null;
    // Any trailing refetch queued against the previous context is redundant:
    // this effect is already issuing a request with the new params.
    pendingRef.current = false;
    generationRef.current += 1;

    setData(null);
    setError(null);
    setStatus("loading");

    fetchGlance();
  }, [cohortId, challengeId, fetchGlance]);

  // Onboarding completion announces itself through the existing adminDashboard
  // refreshTrigger. It follows a write, so it is authoritative and must not be
  // dropped mid-poll. Coach's own squad-level selector is gone (spec 1.5, A2),
  // while the shared Dashboard command band owns the guarded room-level pace
  // control. The trigger is shared: TeachingJourney and Journey/Description still write
  // the level, and this surface should still answer with current state when
  // they do.
  const previousRefreshRef = useRef(refreshTrigger);
  useEffect(() => {
    if (previousRefreshRef.current !== refreshTrigger && refreshTrigger > 0) {
      refetch();
    }
    previousRefreshRef.current = refreshTrigger;
  }, [refreshTrigger, refetch]);

  // Poll on the server's cadence, and only while the tab is actually being
  // looked at. A backgrounded facilitator dashboard should cost nothing.
  const pollSeconds =
    Number(data?.poll_interval_seconds) > 0
      ? Number(data.poll_interval_seconds)
      : FALLBACK_POLL_INTERVAL_SECONDS;

  useEffect(() => {
    let intervalId = null;

    const start = () => {
      if (intervalId !== null) return;
      // Plain call, no queueIfBusy: a tick that lands mid-request is dropped.
      intervalId = setInterval(() => fetchGlance(), pollSeconds * 1000);
    };

    const stop = () => {
      if (intervalId === null) return;
      clearInterval(intervalId);
      intervalId = null;
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
        return;
      }
      // Coming back to the tab is a request for current state, not a tick.
      refetch();
      start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [fetchGlance, refetch, pollSeconds]);

  return { data, status, error, refetch, refreshSnapshot };
};

export default useCoachGlance;
